import React, { Suspense } from 'react';
import { Route, Redirect, Switch, useLocation, Link } from 'react-router-dom';

import * as Sentry from '@sentry/react';

import { map } from 'lodash';

import { compose, Activate } from 'common';
import Loading from 'common/components/Loading';
import lazy from 'common/utils/lazy';

import withApi from 'common/hocs/withApi';
import withUser from 'common/hocs/withUser';

import Content from 'common/components/Content';

import logger from 'itrvl-logger';
const log = logger(__filename);
log.trace(__filename);
const Config = lazy(() => import('common/components/Config'), { chunkName: 'Config' });

const Enquire = lazy(() => import('./components/enquire/Enquire'), { chunkName: 'Enquire' });
const Enquire2 = lazy(() => import('./components/enquire/Enquire2'), { chunkName: 'Enquire2' });
const Enquire3 = lazy(() => import('./components/enquire/Enquire3'), { chunkName: 'Enquire3' });
const Enquire4 = lazy(() => import('./components/enquire/Enquire4'), { chunkName: 'Enquire4' });
const Payment = lazy(() => import('./components/payment/Payment'), { chunkName: 'Payment' });
const Country = lazy(() => import('./components/Country'), { chunkName: 'Country' });
const RegionInfo = lazy(() => import('./components/RegionInfo'), { chunkName: 'RegionInfo' });
const ActivityInfo = lazy(() => import('./components/ActivityInfo'), { chunkName: 'ActivityInfo' });
const LoginScreen = lazy(() => import('./components/LoginScreen'), { chunkName: 'LoginScreen' });
const ShareItinerary = lazy(() => import('./components/ShareItinerary'), { chunkName: 'ShareItinerary' });
const ViewIcons = lazy(() => import('common/components/Icons/ViewIcons'), { chunkName: 'ViewIcons' });
const ViewIcons2 = lazy(() => import('common/components/Icons/ViewIcons2'), { chunkName: 'ViewIcons2' });

const Portal = lazy(() => import('./components/Portal'), { chunkName: 'Portal' });

// @todo when all views are ported over remove the determine view logic and just use the renderer directly
const ItinerariesPage = lazy(() => import('./pages/ItinerariesPage.js'), { chunkName: 'ItinerariesPage' });
const ItineraryPage = lazy(() => import('./pages/ItineraryPage.js'), { chunkName: 'ItineraryPage' });
const ExampleItineraryPage = lazy(() => import('./pages/ExampleItineraryPage.js'), { chunkName: 'ExampleItineraryPage' });
const QuestionsPage = lazy(() => import('./pages/QuestionsPage.js'), { chunkName: 'QuestionsPage' });
const CampPage = lazy(() => import('./pages/CampPage.js'), { chunkName: 'CampPage' });
const ProfilePage = lazy(() => import('./pages/ProfilePage.js'), { chunkName: 'ProfilePage' });
const BookingsPage = lazy(() => import('./pages/BookingsPage'), { chunkName: 'BookingsPage' });

const SentryRoute = Sentry.withSentryRouting(Route);

const SentryError = props => {
  const pretag = props.history.action === 'PUSH' ? '' : 'web-';
  return (
    <SentryRoute
      {...props}
      component={() => {
        throw new Error(`throw.sentry.${pretag}client`);
      }}
    />
  );
};

const AuthenticatedRoute = ({ component: Component, userContext, ...rest }) => {
  const { user } = userContext;
  return (
    <SentryRoute
      {...rest}
      render={props =>
        !!user ? (
          <Component {...rest} user={user} />
        ) : (
          <Redirect
            to={{
              pathname: '/client/login',
              state: { from: props.location },
            }}
          />
        )
      }
    />
  );
};

const NonAuthenticatedRoute = ({ component: Component, userContext, ...rest }) => {
  const { user } = userContext;
  return <SentryRoute {...rest} render={props => <Component {...rest} {...props} user={user} />} />;
};

// Exposed for SSR render
export const routes = [
  {
    name: 'sentryError',
    path: '/client/sentryError',
    exact: true,
    component: SentryError,
  },
  {
    name: 'example-itinerary-camp',
    exact: true,
    path: `/client/example/:savedItineraryId/:agencyId/:agentId/camp/:camp`,
    component: CampPage,
    view: 'campInfo',
  },
  {
    name: 'enquire',
    path: `/client/example/:savedItineraryId/:agencyId/:agentId/enquire`,
    component: Enquire,
    authenticated: false,
  },
  {
    name: 'enquire2',
    path: `/client/example/:savedItineraryId/:agencyId/:agentId/enquire2`,
    component: Enquire2,
    authenticated: false,
  },
  {
    name: 'enquire3',
    path: `/client/example/:savedItineraryId/:agencyId/:agentId/enquire3`,
    component: Enquire3,
    authenticated: false,
  },
  {
    name: 'enquire4',
    path: `/client/example/:savedItineraryId/:agencyId/:agentId/enquire4`,
    component: Enquire4,
    authenticated: false,
  },
  {
    name: 'questions-itinerary',
    exact: true,
    path: '/client/itineraries/:id/questions',
    component: QuestionsPage,
    view: 'campInfo',
  },
  {
    name: 'itinerary-camp',
    exact: true,
    path: '/client/itineraries/:id/camp/:camp',
    component: CampPage,
    view: 'campInfo',
  },
  {
    name: 'payItinerary',
    authenticated: true,
    exact: true,
    path: '/client/itineraries/:id/pay',
    component: Payment,
  },
  {
    name: 'itinerary',
    authenticated: true,
    exact: true,
    path: '/client/itineraries/:id',
    component: ItineraryPage,
  },

  {
    name: 'itineraries',
    authenticated: true,
    exact: true,
    path: '/client/itineraries',
    component: ItinerariesPage,
  },
  {
    name: 'share',
    authenticated: false,
    exact: true,
    path: '/client/share/:id',
    component: ShareItinerary,
  },
  {
    name: 'questions',
    authenticated: false,
    exact: true,
    path: '/client/example/:savedItineraryId/:agencyId/:agentId/questions',
    component: QuestionsPage,
  },
  {
    name: 'example',
    path: `/client/example/:savedItineraryId/:agencyId/:agentId`,
    authenticated: false,
    component: ExampleItineraryPage,
  },
  {
    name: 'login',
    exact: true,
    authenticated: false,
    path: '/client/:route(login|forgot|forgotten|loggedOut|reset)/:token?',
    component: LoginScreen,
  },
  {
    name: 'country',
    exact: true,
    path: '/client/country/:country',
    component: Country,
  },
  {
    name: 'region',
    exact: true,
    path: '/client/region/:region',
    component: RegionInfo,
  },
  {
    name: 'camp',
    exact: true,
    path: '/client/camp/:camp',
    component: CampPage,
    view: 'campInfo',
  },
  {
    name: 'activity',
    exact: true,
    path: '/client/activity/:activity',
    component: ActivityInfo,
  },

  {
    name: 'bookings',
    authenticated: true,
    exact: true,
    path: '/client/bookings',
    component: BookingsPage,
  },

  {
    name: 'profile',
    authenticated: true,
    exact: true,
    path: '/client/profile',
    component: ProfilePage,
  },
  {
    name: 'questions',
    authenticated: false,
    exact: true,
    path: '/client/questions',
    component: QuestionsPage,
  },
  {
    name: 'activate',
    path: `/client/activate/:token`,
    render: props => (
      <Activate
        {...props}
        onActivate={(...args) => props.Api.verifyToken(...args)}
        onSetClientPassword={(...args) => props.Api.setClientInitialPassword(...args)}
        onSuccess={() => props.history.push(`/client/activated`)}
      />
    ),
  },
  {
    name: 'portal',
    path: `/client/portal/:token/:itinerary?/:action?`,
    component: Portal,
    authenticated: false,
  },
  {
    name: 'config',
    path: '/config',
    exact: true,
    component: Config,
    authenticated: true,
  },
  {
    name: 'client',
    path: '/client',
    exact: true,
    redirect: '/client/itineraries',
  },
  {
    name: 'root',
    path: '/',
    exact: true,
    redirect: '/client',
  },
  {
    name: 'root-login',
    path: '/login',
    exact: true,
    redirect: '/client',
  },
];

if (process.env.NODE_ENV === 'development') {
  // Place this before catch all.
  routes.unshift({
    name: 'viewIcons',
    path: '/client/icons',
    component: ViewIcons,
  });
  routes.unshift({
    name: 'viewIcons2',
    path: '/client/icons2',
    component: ViewIcons2,
  });
}

function buildRoute(route, props) {
  let ret;
  if (route.authenticated) {
    ret = <AuthenticatedRoute key={route.name} {...props} {...route} />;
  } else if (route.redirect) {
    ret = <Redirect key={route.name} {...props} to={{ pathname: route.redirect, state: { from: props.location } }} />;
  } else {
    ret = <NonAuthenticatedRoute key={route.name} {...props} {...route} />;
  }
  return ret;
}

const NoMatch = () => {
  const location = useLocation();

  return (
    <div>
      <h3>
        No match for <code>{location.pathname}</code> was found.
      </h3>
      <p>
        <Link to="/">Try going home?</Link>
      </p>
    </div>
  );
};

const Routes = props => {
  return (
    <Content>
      <Suspense fallback={<Loading />}>
        <Switch>
          {map(routes, route => buildRoute(route, props))}
          <SentryRoute path="*">
            <NoMatch />
          </SentryRoute>
        </Switch>
      </Suspense>
    </Content>
  );
};

export default compose(withApi, withUser)(Routes);
